import React, { useState, useEffect } from 'react';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  Squares2X2Icon,
  ListBulletIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  CubeIcon,
  ChartBarIcon,
  BanknotesIcon,
  CalendarIcon,
  CogIcon,
} from '@heroicons/react/24/outline';
import { useSupplies } from '../hooks/useSupplies';
import PageHero from '../components/ui/PageHero';
import {
  Supply,
  SupplyType,
  SupplyUnit,
  SupplyStatus,
  CreateSupplyDto,
  UpdateSupplyDto,
  getSupplyTypeLabel,
  getSupplyUnitLabel,
  getSupplyStatusLabel,
  getSupplyStatusColor,
  getStockLevelColor,
  formatSupplyValue,
  formatSupplyQuantity,
} from '../api/supplies';
import SupplyForm from '../components/SupplyForm';
import SupplyIntegrationPanel from '../components/SupplyIntegrationPanel';
import SuppliesInventoryTrendAnalytics from '../components/SuppliesInventoryTrendAnalytics';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from '../components/ui/BrandModal';

const Supplies: React.FC = () => {
  const {
    supplies,
    loading,
    error,
    statistics,
    createSupply,
    updateSupply,
    deleteSupply,
    refreshData,
    loadSupplies,
  } = useSupplies();

  // State for UI
  const [activeTab, setActiveTab] = useState<'inventory' | 'integration'>('inventory');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState<SupplyType | 'all'>('all');
  const [selectedStatus, setSelectedStatus] = useState<SupplyStatus | 'all'>('all');
  const [selectedLocation, setSelectedLocation] = useState<string>('all');
  const [selectedSupplier, setSelectedSupplier] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [showFilters, setShowFilters] = useState(false);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedSupply, setSelectedSupply] = useState<Supply | null>(null);
  const [formLoading, setFormLoading] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  // Get unique values for filters
  const uniqueLocations = Array.from(new Set(supplies.map(s => s.location).filter(Boolean)));
  const uniqueSuppliers = Array.from(new Set(supplies.map(s => s.supplier).filter(Boolean)));

  // Filter supplies
  const filteredSupplies = supplies.filter(supply => {
    const matchesSearch = supply.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         supply.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         supply.supplier?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         supply.batchNumber?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = selectedType === 'all' || supply.type === selectedType;
    const matchesStatus = selectedStatus === 'all' || supply.status === selectedStatus;
    const matchesLocation = selectedLocation === 'all' || supply.location === selectedLocation;
    const matchesSupplier = selectedSupplier === 'all' || supply.supplier === selectedSupplier;

    return matchesSearch && matchesType && matchesStatus && matchesLocation && matchesSupplier;
  });

  // Paginate supplies
  const totalPages = Math.ceil(filteredSupplies.length / itemsPerPage);
  const paginatedSupplies = filteredSupplies.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Load supplies with filters
  useEffect(() => {
    const filters = {
      search: searchTerm || undefined,
      type: selectedType !== 'all' ? selectedType : undefined,
      status: selectedStatus !== 'all' ? selectedStatus : undefined,
      location: selectedLocation !== 'all' ? selectedLocation : undefined,
      supplier: selectedSupplier !== 'all' ? selectedSupplier : undefined,
    };
    loadSupplies(filters);
  }, [searchTerm, selectedType, selectedStatus, selectedLocation, selectedSupplier, loadSupplies]);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedType, selectedStatus, selectedLocation, selectedSupplier]);

  const handleCreateSupply = async (data: CreateSupplyDto) => {
    setFormLoading(true);
    try {
      const created = await createSupply(data);
      if (created) {
        setShowCreateModal(false);
      }
    } catch (error) {
      console.error('Error creating supply:', error);
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateSupply = async (data: UpdateSupplyDto) => {
    if (!selectedSupply) return;
    
    setFormLoading(true);
    try {
      const updated = await updateSupply(selectedSupply.id, data);
      if (updated) {
        setShowEditModal(false);
        setSelectedSupply(null);
      }
    } catch (error) {
      console.error('Error updating supply:', error);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteSupply = async () => {
    if (!selectedSupply) return;
    
    try {
      const deleted = await deleteSupply(selectedSupply.id);
      if (deleted) {
        setShowDeleteModal(false);
        setSelectedSupply(null);
      }
    } catch (error) {
      console.error('Error deleting supply:', error);
    }
  };

  const getStatusBadgeColor = (status: SupplyStatus) => {
    const color = getSupplyStatusColor(status);
    const colorMap = {
      green: 'bg-emerald-100 text-emerald-800',
      yellow: 'bg-amber-100 text-amber-800',
      red: 'bg-rose-100 text-rose-800',
      gray: 'bg-violet-100 text-violet-800',
    };
    return colorMap[color as keyof typeof colorMap] || colorMap.gray;
  };

  const getStockLevelBadgeColor = (stockLevel: string) => {
    const color = getStockLevelColor(stockLevel);
    const colorMap = {
      green: 'bg-emerald-100 text-emerald-800',
      yellow: 'bg-amber-100 text-amber-800',
      red: 'bg-rose-100 text-rose-800',
      blue: 'bg-cyan-100 text-cyan-800',
      gray: 'bg-violet-100 text-violet-800',
    };
    return colorMap[color as keyof typeof colorMap] || colorMap.gray;
  };

  const formatDate = (date?: Date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString();
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedType('all');
    setSelectedStatus('all');
    setSelectedLocation('all');
    setSelectedSupplier('all');
  };

  if (loading && supplies.length === 0) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600'></div>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <PageHero
        title="Supplies"
        subtitle="Supply inventory with full system integration"
        actions={
          <button type="button" onClick={() => setShowCreateModal(true)} className="btn btn-primary focus-pop">
            <PlusIcon className="h-5 w-5" />
            Add Supply
          </button>
        }
      />

      {/* Tab Navigation */}
      <div className='border-b border-gray-200'>
        <nav className='-mb-px flex space-x-8'>
          <button
            onClick={() => setActiveTab('inventory')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'inventory'
                ? 'border-blue-500 text-cyan-600'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-gray-300'
            }`}
          >
            <div className='flex items-center space-x-2'>
              <CubeIcon className='h-5 w-5' />
              <span>Inventory Management</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('integration')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'integration'
                ? 'border-blue-500 text-cyan-600'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-gray-300'
            }`}
          >
            <div className='flex items-center space-x-2'>
              <CogIcon className='h-5 w-5' />
              <span>System Integration</span>
            </div>
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'inventory' && (
        <>
          {/* Statistics Overview */}
          <div className='space-y-6'>
        <div className='bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden'>
          <div className='px-6 py-4 border-b border-gray-200'>
            <div className='flex items-center'>
              <CubeIcon className='h-5 w-5 text-cyan-600 mr-2' />
              <h2 className='text-lg font-semibold text-slate-900'>Supplies Analytics</h2>
            </div>
          </div>
          
          <div className='p-6'>
            {/* Primary Metrics */}
            <div className='grid grid-cols-1 md:grid-cols-2 gap-6 mb-8'>
              <div className='bg-cyan-50 p-6 rounded-lg border border-blue-200'>
                <div className='flex items-center justify-between'>
                  <div>
                    <p className='text-sm font-medium text-cyan-600 mb-1'>Total Items</p>
                    <p className='text-3xl font-bold text-blue-900'>{statistics.totalItems}</p>
                    <p className='text-sm text-cyan-700 mt-1'>Items in inventory</p>
                  </div>
                  <div className='bg-blue-100 p-3 rounded-full'>
                    <CubeIcon className='h-8 w-8 text-cyan-600' />
                  </div>
                </div>
              </div>

              <div className='bg-green-50 p-6 rounded-lg border border-green-200'>
                <div className='flex items-center justify-between'>
                  <div>
                    <p className='text-sm font-medium text-green-600 mb-1'>Total Value</p>
                    <p className='text-3xl font-bold text-green-900'>{formatSupplyValue(statistics.totalValue)}</p>
                    <p className='text-sm text-green-700 mt-1'>Current inventory value</p>
                  </div>
                  <div className='bg-green-100 p-3 rounded-full'>
                    <BanknotesIcon className='h-8 w-8 text-green-600' />
                  </div>
                </div>
              </div>
            </div>

            {/* Status Metrics */}
            <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
              <div className='bg-white p-4 rounded-lg border border-gray-200 hover:shadow-sm transition-shadow'>
                <div className='flex items-center'>
                  <div className='bg-yellow-100 p-2 rounded-lg mr-3'>
                    <ExclamationTriangleIcon className='h-4 w-4 text-yellow-600' />
                  </div>
                  <div>
                    <p className='text-sm font-medium text-slate-500'>Low Stock</p>
                    <p className='text-xl font-semibold text-yellow-600'>{statistics.lowStockItems}</p>
                  </div>
                </div>
              </div>

              <div className='bg-white p-4 rounded-lg border border-gray-200 hover:shadow-sm transition-shadow'>
                <div className='flex items-center'>
                  <div className='bg-red-100 p-2 rounded-lg mr-3'>
                    <ExclamationTriangleIcon className='h-4 w-4 text-red-600' />
                  </div>
                  <div>
                    <p className='text-sm font-medium text-slate-500'>Out of Stock</p>
                    <p className='text-xl font-semibold text-red-600'>{statistics.outOfStockItems}</p>
                  </div>
                </div>
              </div>

              <div className='bg-white p-4 rounded-lg border border-gray-200 hover:shadow-sm transition-shadow'>
                <div className='flex items-center'>
                  <div className='bg-red-100 p-2 rounded-lg mr-3'>
                    <ClockIcon className='h-4 w-4 text-red-600' />
                  </div>
                  <div>
                    <p className='text-sm font-medium text-slate-500'>Expired</p>
                    <p className='text-xl font-semibold text-red-600'>{statistics.expiredItems}</p>
                  </div>
                </div>
              </div>

              <div className='bg-white p-4 rounded-lg border border-gray-200 hover:shadow-sm transition-shadow'>
                <div className='flex items-center'>
                  <div className='bg-orange-100 p-2 rounded-lg mr-3'>
                    <CalendarIcon className='h-4 w-4 text-orange-600' />
                  </div>
                  <div>
                    <p className='text-sm font-medium text-slate-500'>Expiring Soon</p>
                    <p className='text-xl font-semibold text-orange-600'>{statistics.expiringSoonItems}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className='bg-white p-6 rounded-lg shadow-sm border border-gray-200'>
        <div className='flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0'>
          {/* Search */}
          <div className='flex-1 max-w-lg'>
            <div className='flex items-center w-full rounded-lg border border-gray-300 bg-white focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500'>
              <span className='pl-3 pr-2 flex items-center flex-shrink-0 text-slate-400' aria-hidden='true'>
                <MagnifyingGlassIcon className='h-5 w-5' />
              </span>
              <input
                type='text'
                placeholder='Search supplies...'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className='flex-1 min-w-0 pr-4 py-2 border-0 bg-transparent focus:outline-none focus:ring-0'
              />
            </div>
          </div>

          {/* View Mode and Filter Toggle */}
          <div className='flex items-center space-x-4'>
            <div className='flex items-center bg-gray-100 rounded-lg p-1'>
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-md ${viewMode === 'grid' ? 'bg-white shadow-sm' : ''}`}
              >
                <Squares2X2Icon className='h-5 w-5' />
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`p-2 rounded-md ${viewMode === 'table' ? 'bg-white shadow-sm' : ''}`}
              >
                <ListBulletIcon className='h-5 w-5' />
              </button>
            </div>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className='flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-slate-50/80'
            >
              <FunnelIcon className='h-5 w-5' />
              <span>Filters</span>
            </button>
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className='mt-6 pt-6 border-t border-gray-200'>
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4'>
              <div>
                <label className='block text-sm font-medium text-slate-700 mb-2'>Type</label>
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value as SupplyType | 'all')}
                  className='w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                >
                  <option value='all'>All Types</option>
                  {Object.values(SupplyType).map(type => (
                    <option key={type} value={type}>
                      {getSupplyTypeLabel(type)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className='block text-sm font-medium text-slate-700 mb-2'>Status</label>
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value as SupplyStatus | 'all')}
                  className='w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                >
                  <option value='all'>All Statuses</option>
                  {Object.values(SupplyStatus).map(status => (
                    <option key={status} value={status}>
                      {getSupplyStatusLabel(status)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className='block text-sm font-medium text-slate-700 mb-2'>Location</label>
                <select
                  value={selectedLocation}
                  onChange={(e) => setSelectedLocation(e.target.value)}
                  className='w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                >
                  <option value='all'>All Locations</option>
                  {uniqueLocations.map(location => (
                    <option key={location} value={location}>
                      {location}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className='block text-sm font-medium text-slate-700 mb-2'>Supplier</label>
                <select
                  value={selectedSupplier}
                  onChange={(e) => setSelectedSupplier(e.target.value)}
                  className='w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                >
                  <option value='all'>All Suppliers</option>
                  {uniqueSuppliers.map(supplier => (
                    <option key={supplier} value={supplier}>
                      {supplier}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className='mt-4 flex justify-end'>
              <button
                onClick={clearFilters}
                className='px-4 py-2 text-sm text-slate-600 hover:text-gray-800'
              >
                Clear Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className='bg-red-50 border border-red-200 rounded-lg p-4'>
          <p className='text-red-800'>{error}</p>
        </div>
      )}

      {/* Supplies Grid/Table */}
      {viewMode === 'grid' ? (
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'>
          {paginatedSupplies.map((supply) => (
            <div key={supply.id} className='bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden'>
              <div className='p-6'>
                <div className='flex justify-between items-start mb-4'>
                  <h3 className='text-lg font-semibold text-slate-900 truncate'>{supply.name}</h3>
                  <div className='flex space-x-1'>
                    <button
                      onClick={() => {
                        setSelectedSupply(supply);
                        setShowViewModal(true);
                      }}
                      className='p-1 text-slate-400 hover:text-cyan-600'
                    >
                      <EyeIcon className='h-4 w-4' />
                    </button>
                    <button
                      onClick={() => {
                        setSelectedSupply(supply);
                        setShowEditModal(true);
                      }}
                      className='p-1 text-slate-400 hover:text-green-600'
                    >
                      <PencilIcon className='h-4 w-4' />
                    </button>
                    <button
                      onClick={() => {
                        setSelectedSupply(supply);
                        setShowDeleteModal(true);
                      }}
                      className='p-1 text-slate-400 hover:text-red-600'
                    >
                      <TrashIcon className='h-4 w-4' />
                    </button>
                  </div>
                </div>

                <div className='space-y-3'>
                  <div className='flex justify-between items-center'>
                    <span className='text-sm text-slate-500'>Type:</span>
                    <span className='text-sm font-medium'>{getSupplyTypeLabel(supply.type)}</span>
                  </div>

                  <div className='flex justify-between items-center'>
                    <span className='text-sm text-slate-500'>Quantity:</span>
                    <span className='text-sm font-medium'>{formatSupplyQuantity(supply.quantity, supply.unit)}</span>
                  </div>

                  <div className='flex justify-between items-center'>
                    <span className='text-sm text-slate-500'>Value:</span>
                    <span className='text-sm font-medium'>{formatSupplyValue(supply.totalValue)}</span>
                  </div>

                  <div className='flex justify-between items-center'>
                    <span className='text-sm text-slate-500'>Status:</span>
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeColor(supply.status)}`}>
                      {getSupplyStatusLabel(supply.status)}
                    </span>
                  </div>

                  <div className='flex justify-between items-center'>
                    <span className='text-sm text-slate-500'>Stock Level:</span>
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStockLevelBadgeColor(supply.stockLevel)}`}>
                      {supply.stockLevel.charAt(0).toUpperCase() + supply.stockLevel.slice(1)}
                    </span>
                  </div>

                  {supply.supplier && (
                    <div className='flex justify-between items-center'>
                      <span className='text-sm text-slate-500'>Supplier:</span>
                      <span className='text-sm font-medium truncate'>{supply.supplier}</span>
                    </div>
                  )}

                  {supply.expirationDate && (
                    <div className='flex justify-between items-center'>
                      <span className='text-sm text-slate-500'>Expires:</span>
                      <span className={`text-sm font-medium ${supply.isExpired ? 'text-red-600' : supply.isExpiringSoon ? 'text-orange-600' : ''}`}>
                        {formatDate(supply.expirationDate)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className='bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden'>
          <div className='overflow-x-auto'>
            <table className='min-w-full divide-y divide-gray-200'>
              <thead className='bg-slate-50/80'>
                <tr>
                  <th className='px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider'>
                    Name
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider'>
                    Type
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider'>
                    Quantity
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider'>
                    Value
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider'>
                    Status
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider'>
                    Stock Level
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider'>
                    Supplier
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider'>
                    Expiration
                  </th>
                  <th className='px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider'>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className='bg-white divide-y divide-gray-200'>
                {paginatedSupplies.map((supply) => (
                  <tr key={supply.id} className='hover:bg-slate-50/80'>
                    <td className='px-6 py-4 whitespace-nowrap'>
                      <div className='text-sm font-medium text-slate-900'>{supply.name}</div>
                      {supply.description && (
                        <div className='text-sm text-slate-500 truncate max-w-xs'>{supply.description}</div>
                      )}
                    </td>
                    <td className='px-6 py-4 whitespace-nowrap text-sm text-slate-900'>
                      {getSupplyTypeLabel(supply.type)}
                    </td>
                    <td className='px-6 py-4 whitespace-nowrap text-sm text-slate-900'>
                      {formatSupplyQuantity(supply.quantity, supply.unit)}
                    </td>
                    <td className='px-6 py-4 whitespace-nowrap text-sm text-slate-900'>
                      {formatSupplyValue(supply.totalValue)}
                    </td>
                    <td className='px-6 py-4 whitespace-nowrap'>
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeColor(supply.status)}`}>
                        {getSupplyStatusLabel(supply.status)}
                      </span>
                    </td>
                    <td className='px-6 py-4 whitespace-nowrap'>
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStockLevelBadgeColor(supply.stockLevel)}`}>
                        {supply.stockLevel.charAt(0).toUpperCase() + supply.stockLevel.slice(1)}
                      </span>
                    </td>
                    <td className='px-6 py-4 whitespace-nowrap text-sm text-slate-900'>
                      {supply.supplier || 'N/A'}
                    </td>
                    <td className='px-6 py-4 whitespace-nowrap text-sm text-slate-900'>
                      <span className={supply.isExpired ? 'text-red-600' : supply.isExpiringSoon ? 'text-orange-600' : ''}>
                        {formatDate(supply.expirationDate)}
                      </span>
                    </td>
                    <td className='px-6 py-4 whitespace-nowrap text-right text-sm font-medium'>
                      <div className='flex justify-end space-x-2'>
                        <button
                          onClick={() => {
                            setSelectedSupply(supply);
                            setShowViewModal(true);
                          }}
                          className='text-cyan-600 hover:text-blue-900'
                        >
                          <EyeIcon className='h-4 w-4' />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedSupply(supply);
                            setShowEditModal(true);
                          }}
                          className='text-green-600 hover:text-green-900'
                        >
                          <PencilIcon className='h-4 w-4' />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedSupply(supply);
                            setShowDeleteModal(true);
                          }}
                          className='text-red-600 hover:text-red-900'
                        >
                          <TrashIcon className='h-4 w-4' />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className='flex items-center justify-between bg-white px-4 py-3 border border-gray-200 rounded-lg'>
          <div className='flex-1 flex justify-between sm:hidden'>
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className='relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-slate-700 bg-white hover:bg-slate-50/80 disabled:opacity-50 disabled:cursor-not-allowed'
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className='ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-slate-700 bg-white hover:bg-slate-50/80 disabled:opacity-50 disabled:cursor-not-allowed'
            >
              Next
            </button>
          </div>
          <div className='hidden sm:flex-1 sm:flex sm:items-center sm:justify-between'>
            <div>
              <p className='text-sm text-slate-700'>
                Showing{' '}
                <span className='font-medium'>{(currentPage - 1) * itemsPerPage + 1}</span>
                {' '}to{' '}
                <span className='font-medium'>
                  {Math.min(currentPage * itemsPerPage, filteredSupplies.length)}
                </span>
                {' '}of{' '}
                <span className='font-medium'>{filteredSupplies.length}</span>
                {' '}results
              </p>
            </div>
            <div>
              <nav className='relative z-0 inline-flex rounded-md shadow-sm -space-x-px'>
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className='relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-slate-500 hover:bg-slate-50/80 disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  Previous
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                      page === currentPage
                        ? 'z-10 bg-cyan-50 border-blue-500 text-cyan-600'
                        : 'bg-white border-gray-300 text-slate-500 hover:bg-slate-50/80'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className='relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-slate-500 hover:bg-slate-50/80 disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  Next
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Inventory Trend Analytics */}
      <div className="mt-8">
        <SuppliesInventoryTrendAnalytics />
      </div>

        </>
      )}

      {/* Integration Tab Content */}
      {activeTab === 'integration' && (
        <SupplyIntegrationPanel />
      )}

      {/* Modals */}
      <BrandModal open={showCreateModal} onClose={() => setShowCreateModal(false)} maxWidthClass="max-w-4xl">
        <BrandModalHeader title="Add New Supply" onClose={() => setShowCreateModal(false)} />
        <BrandModalBody>
          <SupplyForm
            onSubmit={handleCreateSupply}
            onCancel={() => setShowCreateModal(false)}
            loading={formLoading}
          />
        </BrandModalBody>
      </BrandModal>

      <BrandModal
        open={showEditModal && !!selectedSupply}
        onClose={() => {
          setShowEditModal(false);
          setSelectedSupply(null);
        }}
        maxWidthClass="max-w-4xl"
      >
        <BrandModalHeader
          title="Edit Supply"
          onClose={() => {
            setShowEditModal(false);
            setSelectedSupply(null);
          }}
        />
        <BrandModalBody>
          {selectedSupply && (
            <SupplyForm
              supply={selectedSupply}
              onSubmit={handleUpdateSupply}
              onCancel={() => {
                setShowEditModal(false);
                setSelectedSupply(null);
              }}
              loading={formLoading}
            />
          )}
        </BrandModalBody>
      </BrandModal>

      <BrandModal
        open={showViewModal && !!selectedSupply}
        onClose={() => {
          setShowViewModal(false);
          setSelectedSupply(null);
        }}
        maxWidthClass="max-w-4xl"
      >
        <BrandModalHeader
          title="Supply Details"
          onClose={() => {
            setShowViewModal(false);
            setSelectedSupply(null);
          }}
        />
        <BrandModalBody>
          {selectedSupply && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-500">Name</label>
                    <p className="mt-1 text-sm text-slate-900">{selectedSupply.name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-500">Type</label>
                    <p className="mt-1 text-sm text-slate-900">{getSupplyTypeLabel(selectedSupply.type)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-500">Quantity</label>
                    <p className="mt-1 text-sm text-slate-900">{formatSupplyQuantity(selectedSupply.quantity, selectedSupply.unit)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-500">Unit Price</label>
                    <p className="mt-1 text-sm text-slate-900">{formatSupplyValue(selectedSupply.unitPrice)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-500">Total Value</label>
                    <p className="mt-1 text-sm text-slate-900">{formatSupplyValue(selectedSupply.totalValue)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-500">Status</label>
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeColor(selectedSupply.status)}`}>
                      {getSupplyStatusLabel(selectedSupply.status)}
                    </span>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-500">Supplier</label>
                    <p className="mt-1 text-sm text-slate-900">{selectedSupply.supplier || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-500">Location</label>
                    <p className="mt-1 text-sm text-slate-900">{selectedSupply.location || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-500">Batch Number</label>
                    <p className="mt-1 text-sm text-slate-900">{selectedSupply.batchNumber || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-500">Purchase Date</label>
                    <p className="mt-1 text-sm text-slate-900">{formatDate(selectedSupply.purchaseDate)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-500">Expiration Date</label>
                    <p className={`mt-1 text-sm ${selectedSupply.isExpired ? 'text-red-600' : selectedSupply.isExpiringSoon ? 'text-orange-600' : 'text-slate-900'}`}>
                      {formatDate(selectedSupply.expirationDate)}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-500">Stock Level</label>
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStockLevelBadgeColor(selectedSupply.stockLevel)}`}>
                      {selectedSupply.stockLevel.charAt(0).toUpperCase() + selectedSupply.stockLevel.slice(1)}
                    </span>
                  </div>
                </div>
              </div>

              {selectedSupply.description && (
                <div className="mt-6">
                  <label className="block text-sm font-medium text-slate-500">Description</label>
                  <p className="mt-1 text-sm text-slate-900">{selectedSupply.description}</p>
                </div>
              )}

              {selectedSupply.instructions && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-slate-500">Instructions</label>
                  <p className="mt-1 text-sm text-slate-900">{selectedSupply.instructions}</p>
                </div>
              )}

              {selectedSupply.notes && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-slate-500">Notes</label>
                  <p className="mt-1 text-sm text-slate-900">{selectedSupply.notes}</p>
                </div>
              )}
            </>
          )}
        </BrandModalBody>
        <BrandModalFooter>
          <button
            type="button"
            onClick={() => {
              setShowViewModal(false);
              setSelectedSupply(null);
            }}
            className="btn btn-secondary text-sm"
          >
            Close
          </button>
          <button
            type="button"
            onClick={() => {
              setShowViewModal(false);
              setShowEditModal(true);
            }}
            className="btn btn-primary text-sm"
          >
            Edit Supply
          </button>
        </BrandModalFooter>
      </BrandModal>

      <BrandModal
        open={showDeleteModal && !!selectedSupply}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedSupply(null);
        }}
        maxWidthClass="max-w-md"
      >
        <BrandModalHeader
          title="Delete Supply"
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedSupply(null);
          }}
        />
        <BrandModalBody>
          <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
          </div>
          <p className="text-sm text-slate-600 text-center">
            Are you sure you want to delete &quot;{selectedSupply?.name}&quot;? This action cannot be undone.
          </p>
        </BrandModalBody>
        <BrandModalFooter>
          <button
            type="button"
            onClick={() => {
              setShowDeleteModal(false);
              setSelectedSupply(null);
            }}
            className="btn btn-secondary text-sm"
          >
            Cancel
          </button>
          <button type="button" onClick={handleDeleteSupply} className="btn btn-danger text-sm">
            Delete
          </button>
        </BrandModalFooter>
      </BrandModal>
    </div>
  );
};

export default Supplies; 